import { Platform } from "quasar";
import { usePersistedStore } from "src/stores/persisted-store";

/**
 * Location helper for requests that should carry the customer's coordinates
 * *only when location access has already been granted*.
 *
 * The rule everywhere below: never trigger a permission prompt. A list or
 * category request is not the right moment to ask for location — the app
 * already asks explicitly in the Store Locator / Select Location flows. If we
 * cannot establish that access is granted, we simply send no coordinates.
 */

// How long a fix stays good before we ask the device for a fresh one.
const FIX_TTL = 5 * 60 * 1000;
// Never let a slow GPS fix hold up an API call.
const FIX_TIMEOUT = 8000;
// A "no coordinates" answer is remembered for a shorter while, so paging
// through a list cannot re-run a failing lookup on every page.
const MISS_TTL = 60 * 1000;
// Cordova plugins only exist after `deviceready`; a list can load before it.
const DEVICE_READY_TIMEOUT = 3000;
// Plenty for store-level filtering, and keeps the query string small.
const COORD_PRECISION = 6;

let cached = null;
let inFlight = null;
let deviceReady = null;

function isBrowser() {
  return typeof window !== "undefined" && typeof navigator !== "undefined";
}

function toCoordinates(source) {
  const latitude = Number(source?.latitude);
  const longitude = Number(source?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude === 0 && longitude === 0) return null;
  return {
    latitude: Number(latitude.toFixed(COORD_PRECISION)),
    longitude: Number(longitude.toFixed(COORD_PRECISION)),
  };
}

function readPersistedCoordinates() {
  try {
    return toCoordinates(usePersistedStore().coordinates);
  } catch (error) {
    // store not ready (e.g. called before pinia is installed)
    return null;
  }
}

function persistCoordinates(coordinates) {
  try {
    usePersistedStore().setGeoLocation(coordinates);
  } catch (error) {
    // non-fatal: the coordinates are still returned to the caller
  }
}

// On Cordova the plugins are injected at `deviceready`. A discount list can be
// requested before that fires, which would make us miss the permission plugin
// and fall through to the (unreliable in a webview) Permissions API.
function whenDeviceReady() {
  if (!Platform.is.cordova) return Promise.resolve();
  if (typeof cordova !== "undefined" && cordova?.plugins)
    return Promise.resolve();
  if (deviceReady) return deviceReady;

  deviceReady = new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    document.addEventListener("deviceready", finish, { once: true });
    // bounded: a missing deviceready must not hold the request open
    setTimeout(finish, DEVICE_READY_TIMEOUT);
  });

  return deviceReady;
}

function hasAndroidPermissionPlugin() {
  return (
    Platform.is.cordova &&
    Platform.is.android &&
    typeof cordova !== "undefined" &&
    !!cordova?.plugins?.permissions
  );
}

// cordova-plugin-android-permissions is Android-only; it reports the granted
// state without showing anything to the user.
function checkAndroidPermission() {
  return new Promise((resolve) => {
    const permissions = cordova.plugins.permissions;
    permissions.checkPermission(
      permissions.ACCESS_FINE_LOCATION,
      (fine) => {
        if (fine?.hasPermission) return resolve(true);
        permissions.checkPermission(
          permissions.ACCESS_COARSE_LOCATION,
          (coarse) => resolve(!!coarse?.hasPermission),
          () => resolve(false)
        );
      },
      () => resolve(false)
    );
  });
}

/**
 * @returns {Promise<boolean|null>} true / false when the state is known,
 * null when it cannot be determined without prompting (iOS WKWebView and
 * Safari do not expose geolocation through the Permissions API).
 */
async function isLocationAllowed() {
  if (hasAndroidPermissionPlugin()) return checkAndroidPermission();

  try {
    if (navigator?.permissions?.query) {
      const status = await navigator.permissions.query({ name: "geolocation" });
      if (status?.state === "granted") return true;
      if (status?.state === "denied") return false;
      // "prompt" -> asking here would pop the browser dialog mid-listing
      return false;
    }
  } catch (error) {
    // geolocation is not a queryable permission name on this platform
  }

  return null;
}

function readDevicePosition() {
  return new Promise((resolve) => {
    if (!navigator?.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(toCoordinates(position?.coords)),
      () => resolve(null),
      {
        enableHighAccuracy: false,
        timeout: FIX_TIMEOUT,
        maximumAge: FIX_TTL,
      }
    );
  });
}

// The iOS webview's Permissions API knows nothing about the app's own location
// permission and keeps answering "prompt", so on iOS the check below always
// came back false. Read the device directly instead: with access granted it
// returns silently, when denied it fails silently, and when the customer has
// not decided yet iOS asks once - the same prompt AppFooter already triggers
// on launch, so the Store Locator is no longer needed to get coordinates.
function isCordovaIos() {
  return Platform.is.cordova && Platform.is.ios;
}

async function resolveCoordinates() {
  await whenDeviceReady();

  if (isCordovaIos()) {
    const fix = await readDevicePosition();
    if (fix) persistCoordinates(fix);
    return fix || readPersistedCoordinates();
  }

  const allowed = await isLocationAllowed();

  // Explicitly denied (or would need a prompt): send nothing.
  if (allowed === false) return null;

  const persisted = readPersistedCoordinates();

  // State unknown (iOS webview / Safari). Previously stored coordinates mean
  // the customer allowed location earlier in the app, so they are safe to
  // reuse — but we do not touch the device, which could re-prompt.
  if (allowed === null) return persisted;

  const fix = await readDevicePosition();
  if (fix) persistCoordinates(fix);
  return fix || persisted;
}

/**
 * Coordinates to send with a request, or null when location is not allowed.
 * Concurrent callers share a single lookup, and a fix is reused for FIX_TTL
 * so paging through a list does not hit the GPS on every page.
 */
export async function getAllowedCoordinates() {
  if (!isBrowser()) return null;

  if (cached) {
    const ttl = cached.coordinates ? FIX_TTL : MISS_TTL;
    if (Date.now() - cached.at < ttl) {
      return cached.coordinates ? { ...cached.coordinates } : null;
    }
  }

  if (inFlight) return inFlight;

  inFlight = resolveCoordinates()
    .catch(() => null)
    .then((coordinates) => {
      cached = { coordinates: coordinates || null, at: Date.now() };
      inFlight = null;
      return coordinates || null;
    });

  return inFlight;
}

/**
 * Returns a copy of `params` with latitude/longitude added when location is
 * allowed. Never throws and never blocks longer than FIX_TIMEOUT.
 */
export async function withAllowedCoordinates(params = {}) {
  const coordinates = await getAllowedCoordinates();
  if (!coordinates) return { ...params };
  return {
    ...params,
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
  };
}

// Drops the in-memory fix (used on logout / store switch).
export function resetAllowedCoordinates() {
  cached = null;
  inFlight = null;
}
